import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from './ui/Input';
import Button from './ui/Button';

/**
 * Form for user login
 */
const LoginForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Disable form/button while processing
      const formElement = e.target;
      const submitButton = formElement.querySelector('button[type="submit"]');
      if (submitButton) submitButton.disabled = true;
      
      await login(formData.email, formData.password);
      
      // Check if there's a return destination from the location state
      const returnTo = location.state?.returnTo;
      const returnState = location.state?.returnState;
      
      if (returnTo && returnState) {
        navigate(returnTo, { state: returnState });
      } else {
        navigate('/classes');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle specific error types
      if (err.type === 'user_session_already_exists') {
        // We could just show a message and redirect
        const returnTo = location.state?.returnTo;
        const returnState = location.state?.returnState;
        
        if (returnTo && returnState) {
          navigate(returnTo, { state: returnState });
        } else {
          navigate('/classes');
        }
        return;
      }
      
      setError(err.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
      // Re-enable form if we're still on this page
      const submitButton = document.querySelector('button[type="submit"]');
      if (submitButton) submitButton.disabled = false;
    }
  };
  
  // Navigate to register page
  const handleRegisterClick = () => {
    // Pass along the return destination to register page too
    navigate('/register', { state: location.state });
  };
  
  return (
    <div className="max-w-md w-full mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-white text-center">Log In</h1>
      
      {error && (
        <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded text-red-400">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <Input
          label="Email Address"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="you@example.com"
          required
        />
        
        <Input
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Enter your password"
          required
        />
        
        <Button
          type="submit"
          variant="primary"
          fullWidth
          isLoading={isLoading}
          className="mt-4"
        >
          Log In
        </Button>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-gray-400">
          Don't have an account?{' '}
          <button 
            type="button" 
            onClick={handleRegisterClick}
            className="text-[#7e55f6] hover:underline focus:outline-none"
          >
            Register
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;